import React, { useState, useEffect } from 'react';
import { User } from '../types';
import { friendsAPI, userAPI, getImageUrl } from '../services/api';
import { useAuth } from '../hooks/useAuth';

const FriendsPage: React.FC = () => {
  const [friends, setFriends] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAllUsers, setShowAllUsers] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [friendsData, usersData] = await Promise.all([
        friendsAPI.getFriends(),
        userAPI.getUsers()
      ]);
      setFriends(friendsData);
      setAllUsers(usersData);
    } catch (error) {
      console.error('Veriler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    const delayDebounceFn = setTimeout(async () => {
      try {
        const usersData = await userAPI.getUsers(searchTerm);
        setAllUsers(usersData);
      } catch (error) {
        console.error('Arama hatası:', error);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, loading]);

  const addFriend = async (friendId: string) => {
    try {
      const targetId = friendId;
      await friendsAPI.sendRequest(targetId);
      const newFriend = allUsers.find(u => (u.id || (u as any)._id) === targetId);
      if (newFriend) {
        setFriends(prev => [...prev, newFriend]);
      }
    } catch (error) {
      console.error('Arkadaş eklenemedi:', error);
    }
  };

  const removeFriend = async (friendId: string) => {
    try {
      await friendsAPI.removeFriend(friendId);
      setFriends(prev => prev.filter(f => (f.id || (f as any)._id) !== friendId));
    } catch (error) {
      console.error('Arkadaş çıkarılamadı:', error);
    }
  };

  // Safe ID extraction helper
  const getId = (u: any) => u?.id || u?._id;

  // Filter out friends from the allUsers list to display
  const displayUsers = allUsers.filter(user => 
    !friends.some(friend => getId(friend) === getId(user))
  );

  if (loading) {
    return <div style={styles.container}>Yükleniyor...</div>;
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>👥 Arkadaşlar</h2>
      
      {/* Arkadaşlarım */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h3 style={styles.sectionTitle}>Arkadaşlarım ({friends.length})</h3>
          <button
            onClick={() => setShowAllUsers(!showAllUsers)}
            style={styles.toggleButton}
          >
            {showAllUsers ? '👥 Arkadaşları Göster' : '🌍 Tüm Kullanıcılar'}
          </button>
        </div>
        <div style={styles.grid}>
          {friends.map((friend) => (
            <div key={friend.id || (friend as any)._id} style={styles.card}>
              <img
                src={friend.profileImage ? getImageUrl(friend.profileImage) : '/default-avatar.png'}
                alt={friend.username}
                style={styles.avatar}
              />
              <div style={styles.userInfo}>
                <h4 style={styles.username}>{friend.username}</h4>
                <span style={styles.status}>
                  {friend.isLive ? '🔴 Canlı Yayında' : '🟢 Çevrimiçi'}
                </span>
              </div>
              <div style={styles.cardActions}>
                <button style={styles.messageButton}>
                  💬 Mesaj
                </button>
                <button
                  onClick={() => removeFriend(friend.id)}
                  style={styles.removeButton}
                >
                  ❌ Çıkar
                </button>
              </div>
            </div>
          ))}
          
          {friends.length === 0 && (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>👥</div>
              <p style={styles.emptyTitle}>Henüz arkadaşınız yok</p>
              <p style={styles.emptyMessage}>Aşağıdan yeni arkadaşlar ekleyebilirsiniz</p>
            </div>
          )}
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h3 style={styles.sectionTitle}>
            {showAllUsers ? 'Tüm Kullanıcılar' : 'Yeni Arkadaşlar'} ({displayUsers.length})
          </h3>
          <input
            type="text"
            placeholder="🔍 Kullanıcı ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>
        <div style={styles.grid}>
          {displayUsers.map((potentialFriend) => (
            <div key={potentialFriend.id || (potentialFriend as any)._id} style={styles.card}>
              <img
                src={potentialFriend.profileImage ? getImageUrl(potentialFriend.profileImage) : '/default-avatar.png'}
                alt={potentialFriend.username}
                style={styles.avatar}
              />
              <div style={styles.userInfo}>
                <h4 style={styles.username}>{potentialFriend.username}</h4>
                <span style={styles.status}>
                  {potentialFriend.isLive ? '🔴 Canlı Yayında' : '🟢 Çevrimiçi'}
                </span>
              </div>
              <button
                onClick={() => addFriend((potentialFriend.id || (potentialFriend as any)._id) as string)}
                style={styles.addButton}
              >
                👥 Arkadaş Ekle
              </button>
            </div>
          ))}
          
          {displayUsers.length === 0 && (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>🔍</div>
              <p style={styles.emptyTitle}>
                {searchTerm ? 'Kullanıcı bulunamadı' : 'Kullanıcı bulunmuyor'}
              </p>
              <p style={styles.emptyMessage}>
                {searchTerm ? 'Farklı bir arama terimi deneyin' : 'Daha fazla kullanıcı yakında'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* İstatistikler */}
      <div style={styles.statsSection}>
        <h3 style={styles.sectionTitle}>📊 İstatistikler</h3>
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{friends.length}</div>
            <div style={styles.statLabel}>Arkadaş Sayısı</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{allUsers.length}</div>
            <div style={styles.statLabel}>Toplam Kullanıcı</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{allUsers.filter(u => u.isLive).length}</div>
            <div style={styles.statLabel}>Canlı Yayın</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>
              {Math.round((friends.length / (friends.length + allUsers.length)) * 100)}%
            </div>
            <div style={styles.statLabel}>Arkadaşlık Oranı</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '16px',
    maxWidth: '1200px',
    margin: '0 auto',
    backgroundColor: '#f5f5f5',
    minHeight: '100vh',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#333',
    margin: '0 0 24px 0',
  },
  section: {
    marginBottom: '32px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
  },
  toggleButton: {
    padding: '8px 16px',
    backgroundColor: '#ff007f',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  searchInput: {
    padding: '10px 16px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    width: '250px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
  },
  card: {
    backgroundColor: 'white',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    padding: '20px',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'all 0.3s',
  },
  avatar: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    objectFit: 'cover',
    marginBottom: '12px',
  },
  userInfo: {
    marginBottom: '12px',
  },
  username: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#333',
    margin: '0 0 4px 0',
  },
  status: {
    fontSize: '14px',
    color: '#666',
  },
  cardActions: {
    display: 'flex',
    gap: '8px',
  },
  messageButton: {
    flex: 1,
    backgroundColor: '#ff007f',
    color: 'white',
    border: 'none',
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  removeButton: {
    flex: 1,
    backgroundColor: '#f44336',
    color: 'white',
    border: 'none',
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  addButton: {
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    padding: '10px 16px',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    width: '100%',
    transition: 'all 0.3s',
  },
  emptyState: {
    gridColumn: '1 / -1',
    textAlign: 'center',
    padding: '40px 20px',
    color: '#666',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    margin: '0 0 8px 0',
  },
  emptyMessage: {
    fontSize: '14px',
    margin: 0,
  },
  statsSection: {
    marginBottom: '80px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  },
  statCard: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '12px',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  statValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#ff007f',
    marginBottom: '4px',
  },
  statLabel: {
    fontSize: '14px',
    color: '#666',
  },
};

export default FriendsPage;

